import type { KeyboardEvent } from 'react';
import type { GridRowId, useGridApiContext } from '../lib/dataGrid';

/**
 * A grid cell taking a suggestion on its Tab or Enter (see tabTakesSuggestion):
 * write the option, THEN let the grid act on the key. Handed on at once, the
 * grid would end the edit while the new value is still being validated — and
 * keep the typed text, or refuse to leave the cell.
 *
 * The grid ignores an event whose propagation was stopped, so it gets a fresh
 * one carrying the same key.
 */
export function takeThenHandOn(
    apiRef: ReturnType<typeof useGridApiContext>,
    { id, field }: { id: GridRowId; field: string },
    option: string,
    event: KeyboardEvent<HTMLElement>
) {
    event.preventDefault();
    event.stopPropagation();
    const { key, which, target } = event;
    const api = apiRef.current;
    if (!api) {
        return;
    }
    void Promise.resolve(
        api.setEditCellValue({ id, field, value: option })
    ).then(() => {
        const handedOn = {
            key,
            which,
            target,
            currentTarget: api.getCellElement(id, field),
            shiftKey: false,
            ctrlKey: false,
            metaKey: false,
            altKey: false,
            preventDefault: () => undefined,
            stopPropagation: () => undefined,
            isPropagationStopped: () => false
        };
        api.publishEvent(
            'cellKeyDown',
            api.getCellParams(id, field),
            handedOn as unknown as KeyboardEvent<HTMLElement>
        );
    });
}
